import { uniqWith } from 'es-toolkit';

import { sortBy } from '@paragon/utilities';

import Point2D from './Point2D';

/**
 * Creates the largest convex polygon whose vertices are from this list.
 * Algorithm adapted from: https://en.wikibooks.org/wiki/Algorithm_Implementation/Geometry/Convex_hull/Monotone_chain.
 */
export function findConvexHull(points: readonly Point2D[]): Point2D[] {
  const uniqueSortedPoints = sortBy(
    uniqWith(points, (a, b) => a.equals(b)),
    [(point) => point.x, (point) => point.y],
  );

  if (uniqueSortedPoints.length < 3) throw new Error("Can't find a convex hull from less than three unique points.");

  const lowerHull = findUpperOrLowerHull(uniqueSortedPoints);
  const upperHull = findUpperOrLowerHull(uniqueSortedPoints.toReversed());

  return lowerHull.splice(1).concat(upperHull.splice(1));
}

function findUpperOrLowerHull(orderedPoints: readonly Point2D[]): Point2D[] {
  const stack = [orderedPoints[0], orderedPoints[1]];

  for (let i = 2; i < orderedPoints.length; i++) {
    const point = orderedPoints[i];

    while (stack.length >= 2 && determinant(stack.at(-2)!, stack.at(-1)!, point) <= 0) {
      stack.pop();
    }

    stack.push(point);
  }

  return stack;
}

function determinant(point1: Point2D, point2: Point2D, point3: Point2D) {
  return (point2.x - point1.x) * (point3.y - point1.y) - (point2.y - point1.y) * (point3.x - point1.x);
}
